import { useCallback, useEffect } from 'react';
import { Easing, useSharedValue, withTiming } from 'react-native-reanimated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Drives a typewriter animation by animating a shared value from `0` to
 * `text.length` with linear easing. Each integer boundary represents one
 * character being revealed.
 *
 * @returns A `SharedValue<number>` that represents the current animated index.
 *
 * @example
 * ```tsx
 * const animatedIndex = useTypewriterAnimation({ text: 'Hello', charDelayMs: 50 });
 *
 * // Use with `useTypewriterAnimationCharStyle` for per-character opacity:
 * const style = useTypewriterAnimationCharStyle({ index: 0, animatedIndex });
 * ```
 */
export const useTypewriterAnimation = (params: Params) => {
  const { text, charDelayMs = DEFAULT_CHAR_DELAY_MS } = params;

  const duration = text.length * charDelayMs;

  // ─── Shared Values ───────────────────────────────────────────────────

  const animatedIndex = useSharedValue(0);

  // ─── Handlers ────────────────────────────────────────────────────────

  const startAnimation = useCallback(() => {
    animatedIndex.value = 0;
    animatedIndex.value = withTiming(text.length, {
      duration,
      easing: Easing.linear,
    });
  }, [animatedIndex, duration, text.length]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(startAnimation, [startAnimation]);

  // ─────────────────────────────────────────────────────────────────────

  return animatedIndex;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_CHAR_DELAY_MS = 50;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  text: string;
  charDelayMs?: number;
};
